import { Activity, Ban, BookMarked, Library, Megaphone, Gauge, Send, LayoutTemplate, Filter, Mails, DownloadCloud, SlidersHorizontal, AlertTriangle, HandCoins, ShoppingCart, Workflow, BadgeCheck, Truck, ShieldAlert, PackageSearch, Banknote, Building2, CalendarDays, ClipboardList, Package, Warehouse, Wrench, ClipboardCheck, FileSpreadsheet, FileText, FolderKanban, Home, Inbox, KeyRound, Landmark, LifeBuoy, ListChecks, LogOut, Menu, ScanSearch, Search, Stamp, Users, CalendarClock, ClipboardCheck as CheckInIcon, HandHeart, CalendarRange, Gavel, UsersRound, CalendarCheck, ScrollText, ShieldCheck, Settings2, Scale, FileSignature, Lock, Vote, DoorOpen, Sprout, BarChart3, MessageSquareReply, Smartphone, Route, BellRing, BellDot, Phone, PhoneCall, KeySquare, Handshake, RotateCcw, DoorClosed, UserCheck } from 'lucide-react'
import { Suspense, useEffect, useState, type FormEvent } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { hasCapability, type Capability } from '@bre-hub/contracts'
import { api } from './api'
import { NotificationBell } from './components/Notifications'
import { CallPopLayer } from './components/CallPop'
import { BrandLogo, ThemeSwitch } from './components/Theme'
import { Loading } from './components/ui'
import { NoAccessPage } from './pages/NoAccess'
import { initials, SessionProvider, useMe } from './session'

// Only sections that exist are in the navigation. Spec §5's full map
// (Programming, Revenue, Finance, ...) arrives section by section as each is
// built, never as placeholder links.
// `or` lists other capabilities that also open a section: someone who only
// checks assets out still needs the way in.
export const NAV: { label: string; items: { to: string; label: string; icon: typeof Home; capability: Capability; or?: Capability[]; end?: boolean }[] }[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/', label: 'Home', icon: Home, capability: 'hub.access', end: true },
      { to: '/tasks', label: 'Tasks', icon: ListChecks, capability: 'hub.access' },
      { to: '/approvals', label: 'Approvals', icon: Stamp, capability: 'hub.access' },
      { to: '/requirements', label: 'Requirements', icon: ClipboardCheck, capability: 'requirements.read' },
      { to: '/workflows', label: 'Workflows', icon: Workflow, capability: 'workflows.run', or: ['workflows.manage'] },
      // Your own shifts: anyone signed in may have some.
      { to: '/shifts/mine', label: 'My shifts', icon: CalendarClock, capability: 'hub.access' },
    ],
  },
  {
    label: 'Scheduling',
    items: [
      { to: '/calendar', label: 'Calendar', icon: CalendarRange, capability: 'calendar.read' },
      { to: '/shifts', label: 'Schedule', icon: CalendarDays, capability: 'shifts.read', or: ['shifts.manage'], end: true },
      { to: '/shifts/check-in', label: 'Check-in', icon: CheckInIcon, capability: 'shifts.lead', or: ['shifts.read', 'shifts.manage'] },
      { to: '/shifts/open', label: 'Open shifts', icon: HandHeart, capability: 'shifts.signup' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { to: '/people', label: 'People', icon: Users, capability: 'people.read' },
      { to: '/organizations', label: 'Organizations', icon: Building2, capability: 'people.read' },
      // Call pop (ADR 0038): the call log and the missed calls to call back.
      { to: '/calls', label: 'Calls', icon: PhoneCall, capability: 'calls.read' },
      { to: '/vendors', label: 'Vendors', icon: Truck, capability: 'vendors.read', or: ['vendors.manage'] },
      // People ops: staff and volunteer records are relationships on a
      // person (spec §14), so they sit with People, not with Operations.
      { to: '/workforce', label: 'Staff and volunteers', icon: BadgeCheck, capability: 'workforce.read', or: ['workforce.manage', 'volunteers.manage'] },
    ],
  },
  {
    label: 'Programming',
    items: [
      { to: '/events', label: 'Events', icon: CalendarDays, capability: 'events.read' },
      { to: '/projects', label: 'Projects', icon: FolderKanban, capability: 'projects.read' },
    ],
  },
  {
    // Rentals and sponsorships (ADR 0041): Booking stays the record; Hub is
    // where staff work the pipeline, asking Booking for each change.
    label: 'Rentals and sponsorships',
    items: [
      { to: '/rentals', label: 'Rentals', icon: KeySquare, capability: 'rentals.read' },
      { to: '/sponsorships', label: 'Sponsorships', icon: Handshake, capability: 'sponsorships.read' },
      // Phase 7 (ADR 0046): fulfillment, renewals and what is left to sell.
      { to: '/sponsorships/deliverables', label: 'Deliverables', icon: ListChecks, capability: 'sponsorships.read' },
      { to: '/sponsorships/renewals', label: 'Renewals', icon: CalendarClock, capability: 'sponsorships.read' },
      { to: '/sponsorships/opportunities', label: 'Opportunities', icon: Package, capability: 'sponsorships.read' },
      // Phase 5 (ADR 0045): Booking's open invoices, and refunds and discounts asked for in Hub.
      { to: '/collections', label: 'Collections', icon: Banknote, capability: 'rentals.money' },
      { to: '/money-requests', label: 'Refunds and discounts', icon: RotateCcw, capability: 'rentals.money', or: ['refunds.approve'] },
      // Phase 8 (ADR 0047): whether Booking's customer portal can retire.
      { to: '/rentals/portal-retirement', label: 'Portal retirement', icon: DoorClosed, capability: 'rentals.read' },
      { to: '/rentals/reports', label: 'Rental reports', icon: BarChart3, capability: 'rentals.money', or: ['finance.read'] },
      { to: '/sponsorships/reports', label: 'Sponsorship reports', icon: BarChart3, capability: 'sponsorships.read' },
      { to: '/rentals/history/shows', label: 'Show history', icon: ScrollText, capability: 'rentals.read' },
    ],
  },
  {
    // Grants (ADR 0019): operational tracking; QuickBooks holds the money.
    label: 'Funding',
    items: [{ to: '/grants', label: 'Grants', icon: HandCoins, capability: 'grants.read', or: ['grants.manage'] }],
  },
  {
    // Purchase requests (ADR 0022): the PO itself lives in QuickBooks.
    label: 'Purchasing',
    items: [{ to: '/purchase-requests', label: 'Purchase requests', icon: ShoppingCart, capability: 'purchasing.request', or: ['purchasing.read', 'purchasing.manage'] }],
  },
  {
    // Governance (ADR 0028): native to Hub; directors use myBRE.
    label: 'Governance',
    items: [
      { to: '/governance', label: 'Overview', icon: Gavel, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'], end: true },
      { to: '/governance/board', label: 'Board', icon: UsersRound, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/committees', label: 'Committees', icon: Users, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/meetings', label: 'Meetings', icon: CalendarCheck, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      // ADR 0065: what is tabled, postponed, carried or referred.
      { to: '/governance/open-items', label: 'Open items', icon: ClipboardList, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      // The resolution book (ADR 0048).
      { to: '/governance/resolutions', label: 'Resolutions', icon: BookMarked, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/minutes', label: 'Minutes', icon: FileSignature, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      // The record book (ADR 0063).
      { to: '/governance/records', label: 'Records', icon: Library, capability: 'records.read', or: ['records.manage', 'records.confidential', 'records.privileged'] },
      { to: '/governance/elections', label: 'Elections', icon: Vote, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/vacancies', label: 'Vacancies', icon: DoorOpen, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/recruitment', label: 'Recruitment', icon: Sprout, capability: 'governance.manage' },
      { to: '/governance/reports', label: 'Reports', icon: BarChart3, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/manuals', label: 'Governing documents', icon: BookMarked, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/policies', label: 'Policies', icon: ScrollText, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/disclosures', label: 'Disclosures', icon: Lock, capability: 'governance.confidential' },
      { to: '/governance/compliance', label: 'Compliance', icon: ShieldCheck, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
      { to: '/governance/settings', label: 'Settings', icon: Settings2, capability: 'governance.read', or: ['governance.manage', 'governance.confidential'] },
    ],
  },
  {
    label: 'Service',
    items: [
      { to: '/cases', label: 'Support', icon: LifeBuoy, capability: 'support.read' },
      { to: '/forms', label: 'Forms', icon: FileText, capability: 'forms.manage', end: true },
      { to: '/forms/submissions', label: 'Form submissions', icon: Inbox, capability: 'forms.review' },
    ],
  },
  {
    // Communications (ADR 0034, 0035): campaigns and the outbox, then
    // consent and suppression.
    label: 'Communications',
    items: [
      { to: '/comms/overview', label: 'Overview', icon: Gauge, capability: 'comms.read' },
      { to: '/comms/campaigns', label: 'Campaigns', icon: Send, capability: 'comms.read' },
      { to: '/comms/journeys', label: 'Journeys', icon: Route, capability: 'comms.read' },
      { to: '/comms/alerts', label: 'Event alerts', icon: BellRing, capability: 'comms.read' },
      { to: '/comms/templates', label: 'Templates', icon: LayoutTemplate, capability: 'comms.read' },
      { to: '/comms/segments', label: 'Segments', icon: Filter, capability: 'comms.read' },
      { to: '/comms/messages', label: 'Messages', icon: Mails, capability: 'comms.read' },
      { to: '/comms/replies', label: 'Text replies', icon: MessageSquareReply, capability: 'comms.read' },
      { to: '/comms', label: 'Consent', icon: Megaphone, capability: 'comms.read', end: true },
      { to: '/comms/suppressions', label: 'Suppression list', icon: Ban, capability: 'comms.read' },
      { to: '/comms/imports', label: 'Imports', icon: DownloadCloud, capability: 'comms.read' },
      { to: '/comms/texting', label: 'Texting', icon: Smartphone, capability: 'comms.read' },
      { to: '/comms/push', label: 'Push and journeys', icon: BellDot, capability: 'comms.read' },
      { to: '/comms/settings', label: 'Settings', icon: Settings2, capability: 'comms.read' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/facilities', label: 'Facilities', icon: Warehouse, capability: 'facilities.read' },
      { to: '/assets', label: 'Assets', icon: Package, capability: 'assets.read', or: ['assets.manage', 'assets.checkout'] },
      { to: '/work-orders', label: 'Work orders', icon: Wrench, capability: 'workorders.read', or: ['workorders.manage', 'workorders.work'] },
      { to: '/inspections', label: 'Inspections', icon: ClipboardList, capability: 'inspections.manage', or: ['inspections.perform'] },
      { to: '/incidents', label: 'Incidents', icon: ShieldAlert, capability: 'incidents.report', or: ['incidents.read', 'incidents.manage'] },
      { to: '/lost-found', label: 'Lost and found', icon: PackageSearch, capability: 'lostfound.read', or: ['lostfound.manage'] },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/finance', label: 'Overview', icon: Landmark, capability: 'finance.read', end: true },
      { to: '/finance/exceptions', label: 'Exceptions', icon: AlertTriangle, capability: 'finance.read' },
      { to: '/finance/payouts', label: 'Payouts', icon: Banknote, capability: 'finance.read' },
      { to: '/finance/postings', label: 'Postings', icon: FileSpreadsheet, capability: 'finance.read' },
      { to: '/finance/rental-money', label: 'Rental money', icon: Banknote, capability: 'rentals.money' },
      { to: '/finance/accounts', label: 'Accounts', icon: KeyRound, capability: 'finance.read' },
      { to: '/finance/pos-places', label: 'POS places', icon: Warehouse, capability: 'events.read' },
      // Event settlement (ADR 0039): Hub owns it; the apps feed it numbers.
      { to: '/settlements', label: 'Settlements', icon: Scale, capability: 'events.settle', or: ['finance.read'] },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/system/access', label: 'Access', icon: KeyRound, capability: 'access.manage', end: true },
      { to: '/system/access/positions', label: 'Access by position', icon: UserCheck, capability: 'access.manage' },
      { to: '/system/health', label: 'System health', icon: Activity, capability: 'system.health.read' },
      // Settings → Operations (ADR 0026): open to whoever owns one of its values.
      { to: '/settings/operations', label: 'Operations settings', icon: SlidersHorizontal, capability: 'hub.admin', or: ['purchasing.manage', 'shifts.manage', 'lostfound.manage'] },
      { to: '/settings/calls', label: 'Phone settings', icon: Phone, capability: 'calls.manage' },
      { to: '/settings/rentals', label: 'Rentals settings', icon: Settings2, capability: 'hub.admin', end: true },
      { to: '/settings/rentals/catalog', label: 'Rental catalog', icon: Settings2, capability: 'rentals.manage', or: ['events.settle', 'sponsorships.manage'] },
    ],
  },
]

export function Shell() {
  return (
    <SessionProvider fallback={<Loading full label="Signing you in" />}>
      {(me) => (hasCapability(me.capabilities, 'hub.access') ? <Frame /> : <NoAccessPage />)}
    </SessionProvider>
  )
}

function Frame() {
  const me = useMe()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => setMenuOpen(false), [location.pathname])

  // "L" opens Look up from anywhere (when not typing).
  const navigate = useNavigate()
  const canLookUp = hasCapability(me.capabilities, 'customers.read')
  useEffect(() => {
    if (!canLookUp) return
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (event.ctrlKey || event.metaKey || event.altKey || (event.key !== 'l' && event.key !== 'L')) return
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return
      if (document.querySelector('[role="dialog"]')) return
      event.preventDefault()
      if (window.location.pathname === '/lookup') document.getElementById('lookup-q')?.focus()
      else navigate('/lookup')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canLookUp, navigate])

  const groups = NAV.map((group) => ({ ...group, items: group.items.filter((item) => [item.capability, ...(item.or ?? [])].some((capability) => hasCapability(me.capabilities, capability))) })).filter(
    (group) => group.items.length > 0,
  )

  async function signOut() {
    const { redirectTo } = await api.signOut()
    window.location.assign(redirectTo)
  }

  return (
    <div className="shell">
      <aside className={`sidebar${menuOpen ? ' open' : ''}`} aria-label="Main navigation">
        <NavLink to="/" className="brand" aria-label="BRE Hub home">
          <BrandLogo />
          <span className="brand-rule" aria-hidden />
          <span className="brand-name">
            Hub
            {me.environment !== 'production' && <span className="env-badge">Dev</span>}
          </span>
        </NavLink>
        {groups.map((group) => (
          <nav className="nav-group" key={group.label} aria-label={group.label}>
            <div className="nav-label">{group.label}</div>
            {group.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                <item.icon size={17} aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </nav>
        ))}
        <div className="sidebar-footer">
          <div className="me">
            <span className="avatar" aria-hidden>
              {initials(me.displayName)}
            </span>
            <div className="me-text">
              <div className="me-name">{me.displayName}</div>
              <div className="me-links">
                <NavLink to={`/people/${me.personId}`} className="faint">
                  Your record
                </NavLink>
                <span className="faint"> · </span>
                <NavLink to="/settings" className="faint" title="My settings">
                  Settings
                </NavLink>
              </div>
            </div>
            <button type="button" className="btn btn-quiet btn-icon" onClick={signOut} title="Sign out">
              <LogOut size={17} aria-hidden />
              <span className="sr-only">Sign out</span>
            </button>
          </div>
          <ThemeSwitch variant="compact" />
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button type="button" className="btn btn-quiet menu-button" onClick={() => setMenuOpen((open) => !open)} aria-label="Open navigation">
            <Menu size={19} aria-hidden />
          </button>
          <GlobalSearch />
          {hasCapability(me.capabilities, 'customers.read') && (
            <NavLink to="/lookup" className="btn topbar-lookup" title="Look up a customer (L)">
              <ScanSearch size={16} aria-hidden />
              <span className="topbar-lookup-label">Look up</span>
            </NavLink>
          )}
          <NotificationBell />
        </header>
        {/* Pages are lazy chunks (main.tsx); navigation runs in a transition, so the old page stays until the new one is ready. */}
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </div>
      <CallPopLayer />
    </div>
  )
}

// Searches People for now; each new section adds its own results here.
function GlobalSearch() {
  const navigate = useNavigate()
  const [value, setValue] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === '/' && !(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) {
        event.preventDefault()
        document.getElementById('global-search')?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  function submit(event: FormEvent) {
    event.preventDefault()
    navigate(`/search?q=${encodeURIComponent(value.trim())}`)
  }

  return (
    <form className="search" role="search" onSubmit={submit}>
      <Search size={16} aria-hidden />
      <label htmlFor="global-search" className="sr-only">
        Search Hub
      </label>
      <input
        id="global-search"
        className="input"
        placeholder="Search people, events, facilities, assets…   ( / )"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        autoComplete="off"
      />
    </form>
  )
}
